import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
  TablePagination,
  CircularProgress,
  SelectChangeEvent,
  Checkbox,
  Card,
  CardContent,
  CardActions,
  Chip,
  Divider,
  Fade,
  Stack,
  Tooltip,
  useTheme,
  alpha,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  tableCellClasses
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Videocam as VideocamIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { GravacoesService, Gravacao, GravacaoUpdate, GravacaoFilter } from '../../services/gravacoesService';

// Interface para entidades de referência
interface EntidadeSimples {
  id: number;
  nome: string;
}

const GravacoesPage: React.FC = () => {
  const theme = useTheme();
  // Estado de listagem
  const [gravacoes, setGravacoes] = useState<Gravacao[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Estado de referências
  const [emissoras, setEmissoras] = useState<EntidadeSimples[]>([]);
  const [anuncios, setAnuncios] = useState<EntidadeSimples[]>([]);
  const [loadingReferencias, setLoadingReferencias] = useState(false);

  // Estado de filtros
  const [filter, setFilter] = useState<GravacaoFilter>({});
  
  // Estado de formulários
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentGravacao, setCurrentGravacao] = useState<Gravacao | null>(null);
  const [editGravacao, setEditGravacao] = useState<GravacaoUpdate>({});

  // Estado para arquivos
  const [editAudioFile, setEditAudioFile] = useState<File | null>(null);

  // Estado de mensagens
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // Estado de visualização
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');

  // Carregar todas as referências necessárias
  const carregarReferencias = useCallback(async () => {
    try {
      setLoadingReferencias(true);
      // TODO: Implementar chamadas reais às APIs
      
      // Mock de emissoras - Usando mock até que a API esteja disponível
      setEmissoras([
        { id: 1, nome: 'Emissora FM' },
        { id: 2, nome: 'Rádio Cidade' },
        { id: 3, nome: 'Rádio Nacional' },
        { id: 4, nome: 'Cultura FM' }
      ]);
      
    } catch (error) {
      console.error('Erro ao carregar referências:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar dados de referência',
        severity: 'error',
      });
    } finally {
      setLoadingReferencias(false);
    }
  }, []);

  const fetchGravacoes = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      const limit = rowsPerPage;
      const data = await GravacoesService.getGravacoes(filter, skip, limit);
      setGravacoes(data);
      // Obs: Em uma API real, geralmente teríamos um count total para paginação
      // Mas vamos assumir que temos 100 registros para simplificar este exemplo
      setTotalCount(100);
    } catch (error) {
      console.error('Erro ao buscar gravações:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao buscar gravações',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filter]);

  useEffect(() => {
    // Buscar gravações ao iniciar a página ou mudar filtros/paginação
    fetchGravacoes();
    // Carregar referências
    carregarReferencias();
  }, [fetchGravacoes, carregarReferencias]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: keyof GravacaoFilter, value: string | number | undefined) => {
    setFilter((prev: GravacaoFilter) => ({
      ...prev,
      [field]: value || undefined, // Use undefined para remover o filtro vazio
    }));
    setPage(0); // Resetar paginação ao mudar filtro
  };

  const handleDateFilterChange = (field: keyof GravacaoFilter, value: string | undefined) => {
    setFilter((prev: GravacaoFilter) => ({
      ...prev,
      [field]: value || undefined,
    }));
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilter({});
    setPage(0);
  };

  const handleOpenEditDialog = (gravacao: Gravacao) => {
    setCurrentGravacao(gravacao);
    // Inicializar dados para edição
    setEditGravacao({
      datahorainicio: gravacao.datahorainicio,
      datahorafim: gravacao.datahorafim,
      amazonpath: gravacao.amazonpath,
      emissora_id: gravacao.emissora_id,
      anuncio_id: gravacao.anuncio_id,
      duracao: gravacao.duracao
    });
    
    setOpenEditDialog(true);
  };

  const handleEditGravacao = async () => {
    if (!currentGravacao) return;

    try {
      // Verificar campos obrigatórios
      if (!editGravacao.emissora_id || !editGravacao.datahorainicio) {
        setSnackbar({
          open: true,
          message: 'Preencha todos os campos obrigatórios',
          severity: 'error',
        });
        return;
      }

      // TODO: Upload do arquivo de áudio se houver novo arquivo
      const gravacaoData = {
        ...editGravacao
      };

      await GravacoesService.updateGravacao(currentGravacao.id, gravacaoData);
      setOpenEditDialog(false);
      setSnackbar({
        open: true,
        message: 'Gravação atualizada com sucesso',
        severity: 'success',
      });
      // Limpar estados
      setCurrentGravacao(null);
      setEditGravacao({});
      setEditAudioFile(null);
      // Recarregar dados
      fetchGravacoes();
    } catch (error) {
      console.error('Erro ao atualizar gravação:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao atualizar gravação',
        severity: 'error',
      });
    }
  };

  const handleOpenDeleteDialog = (gravacao: Gravacao) => {
    setCurrentGravacao(gravacao);
    setOpenDeleteDialog(true);
  };

  const handleDeleteGravacao = async () => {
    if (!currentGravacao) return;

    try {
      await GravacoesService.deleteGravacao(currentGravacao.id);
      setOpenDeleteDialog(false);
      setSnackbar({
        open: true,
        message: 'Gravação removida com sucesso',
        severity: 'success',
      });
      // Limpar estado
      setCurrentGravacao(null);
      // Recarregar dados
      fetchGravacoes();
    } catch (error) {
      console.error('Erro ao remover gravação:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao remover gravação',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChangeEditGravacao = (field: string, value: any) => {
    setEditGravacao({ ...editGravacao, [field]: value });
  };

  const handleEditAudioFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setEditAudioFile(event.target.files[0]);
    }
  };

  const formatarData = (data: string | undefined) => {
    if (!data) return '-';
    try {
      const date = new Date(data);
      return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return '-';
    }
  };

  const formatarDuracao = (duracao: number | undefined) => {
    if (!duracao) return '-';
    const minutos = Math.floor(duracao / 60);
    const segundos = duracao % 60;
    return `${minutos}:${segundos < 10 ? '0' : ''}${segundos}`;
  };

  return (
    <SidebarLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Gravações
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Gerencie as gravações disponíveis no sistema
        </Typography>
      </Box>

      {/* Filtros */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Emissora</InputLabel>
                  <Select
                    label="Emissora"
                    value={filter.emissora_id ? filter.emissora_id.toString() : ''}
                    onChange={(e: SelectChangeEvent) => 
                      handleFilterChange('emissora_id', e.target.value ? Number(e.target.value) : undefined)
                    }
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {emissoras.length > 0 ? (
                      emissoras.map((emissora) => (
                        <MenuItem key={emissora.id} value={emissora.id.toString()}>
                          {emissora.nome}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>Carregando emissoras...</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Data Início"
                  InputLabelProps={{ shrink: true }}
                  value={filter.data_inicio || ''}
                  onChange={(e) => handleDateFilterChange('data_inicio', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Data Fim"
                  InputLabelProps={{ shrink: true }}
                  value={filter.data_fim || ''}
                  onChange={(e) => handleDateFilterChange('data_fim', e.target.value)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button 
                variant="outlined"
                size="small"
                onClick={handleClearFilters}
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 'medium'
                }}
              >
                Limpar
              </Button>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={viewMode}
                onChange={(_, newValue) => {
                  if (newValue) setViewMode(newValue);
                }}
              >
                <ToggleButton value="list">Lista</ToggleButton>
                <ToggleButton value="grid">Grade</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Lista de gravações */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : viewMode === 'list' ? (
        // Visualização em lista (tabela)
        <Paper elevation={2} sx={{ p: 0, overflow: 'hidden' }}>
          <TableContainer>
            <Table sx={{
              [`& .${tableCellClasses.root}`]: {
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
              }
            }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell>Emissora</TableCell>
                  <TableCell>Anúncio</TableCell>
                  <TableCell>Data Início</TableCell>
                  <TableCell>Data Fim</TableCell>
                  <TableCell>Duração</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gravacoes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      Nenhuma gravação encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  gravacoes.map((gravacao) => (
                    <TableRow key={gravacao.id}>
                      <TableCell>{gravacao.emissora?.nome || '-'}</TableCell>
                      <TableCell>{gravacao.anuncio?.nome || '-'}</TableCell>
                      <TableCell>{formatarData(gravacao.datahorainicio)}</TableCell>
                      <TableCell>{formatarData(gravacao.datahorafim)}</TableCell>
                      <TableCell>{formatarDuracao(gravacao.duracao)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenEditDialog(gravacao)}
                          title="Editar"
                          sx={{ 
                            mx: 0.5,
                            '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(gravacao)}
                          title="Excluir"
                          sx={{ 
                            mx: 0.5,
                            '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.1) }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </Paper>
      ) : (
        // Visualização em grade (cards)
        <Box>
          <Grid container spacing={2}>
            {gravacoes.length === 0 ? (
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                  <Typography>Nenhuma gravação encontrada</Typography>
                </Paper>
              </Grid>
            ) : (
              gravacoes.map((gravacao) => (
                <Grid item xs={12} sm={6} md={4} key={gravacao.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {gravacao.emissora?.nome || 'Gravação'}
                      </Typography>
                      <Box sx={{ display: 'flex', mb: 1, gap: 1, flexWrap: 'wrap' }}>
                        {gravacao.duracao && (
                          <Chip 
                            size="small" 
                            label={formatarDuracao(gravacao.duracao)}
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      
                      <Grid container spacing={1} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary">
                            <b>Anúncio:</b> {gravacao.anuncio?.nome || '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary">
                            <b>Início:</b> {formatarData(gravacao.datahorainicio)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary">
                            <b>Fim:</b> {formatarData(gravacao.datahorafim)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                    <Divider />
                    <CardActions sx={{ justifyContent: 'flex-end', px: 2, py: 1.5 }}>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenEditDialog(gravacao)}
                        sx={{ mr: 1, textTransform: 'none' }}
                      >
                        Editar
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleOpenDeleteDialog(gravacao)}
                        sx={{ textTransform: 'none' }}
                      >
                        Excluir
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <TablePagination
              rowsPerPageOptions={[6, 12, 24]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Itens por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </Box>
        </Box>
      )}

      {/* Dialog de Edição */}
      <Dialog 
        open={openEditDialog} 
        onClose={() => setOpenEditDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          pb: 2
        }}>
          Editar Gravação
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box component="form" sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              {/* Informações Básicas */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" color="primary" sx={{ mb: 1, fontWeight: 'medium' }}>
                  Informações Básicas
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Emissora *</InputLabel>
                  <Select
                    label="Emissora *"
                    value={editGravacao.emissora_id || ''}
                    onChange={(e) => handleChangeEditGravacao('emissora_id', e.target.value)}
                  >
                    <MenuItem value="">Selecione...</MenuItem>
                    {emissoras.map((emissora) => (
                      <MenuItem key={emissora.id} value={emissora.id}>
                        {emissora.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Anúncio</InputLabel>
                  <Select
                    label="Anúncio"
                    value={editGravacao.anuncio_id || ''}
                    onChange={(e) => handleChangeEditGravacao('anuncio_id', e.target.value)}
                  >
                    <MenuItem value="">Selecione...</MenuItem>
                    {anuncios.map((anuncio) => (
                      <MenuItem key={anuncio.id} value={anuncio.id}>
                        {anuncio.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Data e Hora de Início *"
                  required
                  InputLabelProps={{ shrink: true }}
                  value={editGravacao.datahorainicio || ''}
                  onChange={(e) => handleChangeEditGravacao('datahorainicio', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="datetime-local"
                  label="Data e Hora de Fim"
                  InputLabelProps={{ shrink: true }}
                  value={editGravacao.datahorafim || ''}
                  onChange={(e) => handleChangeEditGravacao('datahorafim', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Duração (em segundos)"
                  type="number"
                  value={editGravacao.duracao || ''}
                  onChange={(e) => handleChangeEditGravacao('duracao', parseInt(e.target.value, 10))}
                />
              </Grid>
              
              {/* Arquivo de Mídia */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" color="primary" sx={{ mb: 1, fontWeight: 'medium' }}>
                  Arquivo de Mídia
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<CloudUploadIcon />}
                      sx={{ textTransform: 'none' }}
                    >
                      {editGravacao.amazonpath ? 'Alterar Arquivo' : 'Escolher Arquivo de Áudio'}
                      <input
                        type="file"
                        hidden
                        accept="audio/*"
                        onChange={handleEditAudioFileChange}
                      />
                    </Button>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {editAudioFile 
                        ? editAudioFile.name 
                        : (editGravacao.amazonpath 
                          ? editGravacao.amazonpath.split('/').pop() 
                          : 'Nenhum arquivo associado')}
                    </Typography>
                  </Box>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
          <Button 
            onClick={() => setOpenEditDialog(false)}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 'medium' }}
          >
            Cancelar
          </Button>
          <Button 
            variant="contained"
            onClick={handleEditGravacao}
            disabled={!editGravacao.emissora_id}
            sx={{ textTransform: 'none', fontWeight: 'medium', ml: 1 }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Exclusão */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          backgroundColor: alpha(theme.palette.error.main, 0.05),
          pb: 2
        }}>
          Confirmar Exclusão
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" gutterBottom>
            Deseja realmente excluir esta gravação?
          </Typography>
          <Typography variant="caption" color="error">
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
          <Button 
            onClick={() => setOpenDeleteDialog(false)}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 'medium' }}
          >
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDeleteGravacao}
            sx={{ textTransform: 'none', fontWeight: 'medium', ml: 1 }}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </SidebarLayout>
  );
};

export default GravacoesPage; 